import { FeatureName } from '../../graphql';
import { Feature } from '../domain';
import { useFeatures } from './useFeatures';
import { useCurrentWorkspace } from '../../auth/hooks/useCurrentWorkspace';
import { useCurrentAccount } from '../../auth/hooks/useCurrentAccount';

/**
 * Core feature check logic using a three-tier cascade:
 * 1. Global: If feature.enabled is true, the feature is available to everyone
 * 2. Workspace: If not globally enabled, checks if the user's current workspace is assigned to the feature
 * 3. Account: If not workspace-assigned, checks if the user's account is directly assigned to the feature
 *
 * @param feature - The feature to check
 * @param workspaceId - The current workspace ID
 * @param accountId - The current account ID
 * @returns true if the feature is enabled, false otherwise
 */
export const isFeatureEnabledForContext = (
  feature: Feature | undefined,
  workspaceId: string | undefined,
  accountId: string | undefined
): boolean => {
  if (!feature) {
    return false;
  }

  // 1. If globally enabled, return true
  if (feature.enabled) {
    return true;
  }

  // 2. Check if assigned to current workspace
  if (workspaceId && feature.workspaces) {
    const isWorkspaceAssigned = feature.workspaces.some(
      (w) => w.id === workspaceId
    );
    if (isWorkspaceAssigned) {
      return true;
    }
  }

  // 3. Check if assigned to current account
  if (accountId && feature.accounts) {
    const isAccountAssigned = feature.accounts.some((a) => a.id === accountId);
    if (isAccountAssigned) {
      return true;
    }
  }

  return false;
};

const getWorkspaceId = (
  workspace: { id?: string } | string | null | undefined
): string | undefined => {
  return typeof workspace === 'object' ? workspace?.id : undefined;
};

/**
 * Checks if a feature is enabled for the current user using a three-tier cascade.
 *
 * This cascade allows for flexible feature rollout strategies:
 * - Full rollout: Set enabled: true
 * - Workspace-specific rollout: Assign to specific workspaces
 * - Individual testing: Assign to specific accounts
 *
 * @param featureName - The name of the feature to check
 * @returns true if the feature is enabled for the current user, false otherwise
 */
export const useIsFeatureEnabled = (featureName: FeatureName): boolean => {
  const { data } = useFeatures();
  const currentWorkspace = useCurrentWorkspace();
  const { account } = useCurrentAccount();
  const features: Feature[] = data?.items ?? [];

  if (features.length === 0) {
    return false;
  }

  const feature = features.find((f) => f.name === featureName);
  return isFeatureEnabledForContext(
    feature,
    getWorkspaceId(currentWorkspace),
    account?.id
  );
};

/**
 * Same cascade as `useIsFeatureEnabled`, evaluated against an explicit
 * workspace rather than the caller's *current* one. `useCurrentWorkspace()`
 * is wrong for a subject that can differ from the viewer's active
 * workspace — an asset reached via `/workspaces/:workspaceId/asset/:assetId`
 * or shared across workspaces is the case this exists for.
 *
 * `isLoading` covers the account as well as the features, so a caller can
 * hold on a spinner rather than read the pre-load `false` as a resolved
 * negative — the same trap `useIsFeatureEnabled` has (see its jsdoc above).
 * The account matters because the cascade's third tier evaluates an
 * account-scoped assignment: the two queries resolve in arbitrary order, and
 * when features win the race a `false` computed against a null account is
 * indistinguishable from a real negative. A caller that navigates on it —
 * `assetChatPanel.tsx` redirects to the asset profile — then bounces a
 * viewer whose access came from an account-scoped rollout. Workspace-scoped
 * and global rollouts never consult the account and were never affected.
 *
 * @param featureName - The name of the feature to check
 * @param workspaceId - The workspace to evaluate the feature against
 * @returns `{ isEnabled, isLoading }`
 */
export const useIsFeatureEnabledForWorkspace = (
  featureName: FeatureName,
  workspaceId: string | undefined
): { isEnabled: boolean; isLoading: boolean } => {
  const { data, isLoading } = useFeatures();
  const { account, isLoading: isAccountLoading } = useCurrentAccount();
  const features: Feature[] = data?.items ?? [];
  const feature = features.find((f) => f.name === featureName);
  const loading = isLoading || isAccountLoading;

  return {
    isEnabled:
      !loading &&
      features.length > 0 &&
      isFeatureEnabledForContext(feature, workspaceId, account?.id),
    isLoading: loading,
  };
};

/**
 * Returns a function that checks if a feature is enabled for the current user using a three-tier cascade.
 * This is useful when you need to check multiple features or check features conditionally.
 *
 * See useIsFeatureEnabled for documentation on the cascade logic.
 *
 * @returns A function that takes a featureName and returns true if enabled, false otherwise
 */
export const useIsFeatureEnabledFunc = () => {
  const { data } = useFeatures();
  const currentWorkspace = useCurrentWorkspace();
  const { account } = useCurrentAccount();
  const features: Feature[] = data?.items ?? [];

  const isFeatureEnabled = (featureName: FeatureName): boolean => {
    if (features.length === 0) {
      return false;
    }

    const feature = features.find((f) => f.name === featureName);
    return isFeatureEnabledForContext(
      feature,
      getWorkspaceId(currentWorkspace),
      account?.id
    );
  };

  return isFeatureEnabled;
};
